import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';
import {
  addErrorFiles,
  addPreviousSessionProcessingFiles,
  addPreviousSessionUploadingFiles,
  addUploadFilesSuccess,
  chunkUploadFailure,
  chunkUploadSuccess,
  clearUploaderStateSuccess,
  fileUploadSuccess,
  removeFile,
  setCreatedAssets,
  setCurrentUploadId,
  toggleViewUploader,
  updateProcessingFilesStatusesByProcessingResult,
  updateUploadProgress,
  updateUploadRemainingTime,
} from '@shared/components/uploader/store/uploader.actions';
import type { InputFile } from '@shared/components/uploader/interfaces/files.interface';

export interface UploaderState {
  currentUploadId: null | string;
  files: InputFile[];
  previousSessionFiles: InputFile[];
  errorFiles: InputFile[];
  progress: number | null;
  remainingTime: number | null;
  isMinimized: boolean;
  createdAssets: Record<string, { assetType: string; assetId: string }>;
}

export const initialUploaderState: UploaderState = {
  currentUploadId: null,
  files: [],
  previousSessionFiles: [],
  errorFiles: [],
  progress: null,
  remainingTime: null,
  isMinimized: false,
  createdAssets: {},
};
export const uploaderReducer = createImmerReducer(
  initialUploaderState,
  on(updateUploadProgress, (state, { progress }) => {
    state.progress = progress;
    return state;
  }),
  on(updateUploadRemainingTime, (state, { remainingTime }) => {
    state.remainingTime = remainingTime;
    return state;
  }),
  on(toggleViewUploader, (state) => {
    state.isMinimized = !state.isMinimized;
    return state;
  }),
  on(addUploadFilesSuccess, (state, { files }) => {
    state.files = (state?.files || []).concat(files);
    return state;
  }),
  on(addErrorFiles, (state, { files }) => {
    state.errorFiles = files;
    return state;
  }),
  on(addPreviousSessionUploadingFiles, (state, { files }) => {
    state.previousSessionFiles = files;
    return state;
  }),
  on(addPreviousSessionProcessingFiles, (state, { files }) => {
    state.files = files;
    return state;
  }),
  on(removeFile, (state, { input }) => {
    state.files = state.files?.filter(
      (file) => file.uploadId !== input.uploadId,
    );
    return state;
  }),
  on(setCurrentUploadId, (state, { uploadId }) => {
    state.currentUploadId = uploadId;
    const index = state.files?.findIndex((item) => item.uploadId === uploadId);
    if (index !== -1 && state.files?.[index]) {
      state.files[index].status = 'uploading';
    }
    return state;
  }),
  on(fileUploadSuccess, (state, { uploadId }) => {
    const index = state.files?.findIndex((item) => item.uploadId === uploadId);
    if (index !== -1) {
      const fileState = state.files?.[index];
      fileState.status = 'processing';
    }
    return state;
  }),
  on(chunkUploadSuccess, (state, { uploadId, chunkNumber, isCompleted }) => {
    const index = state.files?.findIndex((item) => item.uploadId === uploadId);
    if (index !== -1) {
      const fileState = state.files?.[index];
      fileState.readyChunks[chunkNumber] = true;
      fileState.chunkNumber = chunkNumber;
      if (isCompleted) {
        fileState.isCompleted = true;
        fileState.readyChunks = {};
      }
    }
    return state;
  }),
  on(updateProcessingFilesStatusesByProcessingResult, (state, { files }) => {
    const filesMap = new Map(
      files.map((file) => [
        file.uploadId,
        { status: file.status, error: file.error },
      ]),
    );
    state.files = state.files.map((file) => {
      if (filesMap.has(file.uploadId)) {
        const updatedFile = filesMap.get(file.uploadId);
        if (!updatedFile) return file;
        return {
          ...file,
          status: updatedFile.status,
          error: updatedFile.error,
        };
      }
      return file;
    });
    return state;
  }),
  on(setCreatedAssets, (state, { assets }) => {
    state.createdAssets = assets;
    return state;
  }),
  on(chunkUploadFailure, (state, { uploadId, error }) => {
    const fileState = state.files?.find((file) => file.uploadId === uploadId);
    if (fileState) {
      fileState.status = 'error';
      fileState.error = error;
    }
    return state;
  }),
  on(clearUploaderStateSuccess, () => {
    return initialUploaderState;
  }),
);
